import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { del, get, post } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

const EMPTY_FORM = {
  product: null,
  start_date: '',
  end_date: '',
};

export default function ManageKioskPrimeProductsPage() {
  const [rows, setRows] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [productInput, setProductInput] = React.useState('');
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editForm, setEditForm] = React.useState({ start_date: '', end_date: '', is_active: true });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/kiosk-prime-products');
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load kiosk prime products');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!productInput || productInput.length < 2) {
      setProducts([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: productInput, limit: 50 });
        if (!ignore) setProducts(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setProducts([]);
      }
    })();
    return () => { ignore = true; };
  }, [productInput]);

  const onSave = async () => {
    setError('');
    setOk('');
    const productId = Number(form.product?.id || 0);
    const startDate = String(form.start_date || '').trim();
    const endDate = String(form.end_date || '').trim();
    if (!productId) {
      setError('Select a product.');
      return;
    }
    if (!startDate) {
      setError('Start date is required.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date cannot be before start date.');
      return;
    }
    setSaving(true);
    try {
      await post('/api/manage/kiosk-prime-products', {
        product_id: productId,
        start_date: startDate,
        end_date: endDate,
        is_active: true,
      });
      setOk('Kiosk prime product saved.');
      setForm(EMPTY_FORM);
      setProductInput('');
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to save kiosk prime product');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete prime product "${row.product_name || row.product_id}"?`)) return;
    setError('');
    setOk('');
    try {
      await del(`/api/manage/kiosk-prime-products/${row.id}`);
      setOk('Kiosk prime product deleted.');
      await load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({
      start_date: String(row?.start_date || '').slice(0, 10),
      end_date: row?.end_date ? String(row.end_date).slice(0, 10) : '',
      is_active: row?.is_active !== false,
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingRow(null);
  };

  const onEditSave = async () => {
    if (!editingRow?.product_id) {
      setError('Invalid product for edit.');
      return;
    }
    const startDate = String(editForm.start_date || '').trim();
    const endDate = String(editForm.end_date || '').trim();
    if (!startDate) {
      setError('Start date is required.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date cannot be before start date.');
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      await post('/api/manage/kiosk-prime-products', {
        product_id: Number(editingRow.product_id),
        start_date: startDate,
        end_date: endDate,
        is_active: !!editForm.is_active,
      });
      setOk('Kiosk prime product updated.');
      closeEdit();
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update kiosk prime product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Kiosk Prime Products</Typography>
          <Typography variant="body2" color="text.secondary">
            Promote selected products in kiosk between configured start and end dates.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      {error ? <Typography color="error" sx={{ mb: 1.5 }}>{error}</Typography> : null}
      {ok ? <Typography color="success.main" sx={{ mb: 1.5 }}>{ok}</Typography> : null}

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 320 }}
            options={products}
            value={form.product}
            onChange={(_, v) => setForm((prev) => ({ ...prev, product: v }))}
            inputValue={productInput}
            onInputChange={(_, v) => setProductInput(v)}
            getOptionLabel={(o) => (o?.name ? o.name : '')}
            renderInput={(params) => <TextField {...params} label="Product" size="small" placeholder="Type product name" />}
          />
          <TextField
            label="Start date"
            type="date"
            size="small"
            value={form.start_date}
            onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={form.end_date}
            onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Add'}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No kiosk prime products configured.</TableCell></TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Stack spacing={0.25}>
                    {row.product_url ? (
                      <Typography
                        component="a"
                        href={row.product_url}
                        target="_blank"
                        rel="noreferrer"
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {row.product_name || `#${row.product_id}`}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.product_name || `#${row.product_id}`}</Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{row.start_date ? new Date(row.start_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{row.end_date ? new Date(row.end_date).toLocaleDateString() : 'No end date'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.active_now ? 'Active Now' : (row.is_active ? 'Scheduled/Expired' : 'Inactive')}
                    color={row.active_now ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </Button>
                    <Button size="small" color="error" variant="contained" onClick={() => onDelete(row)}>Delete</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Kiosk Prime Product</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Product"
              value={editingRow?.product_name || `#${editingRow?.product_id || ''}`}
              fullWidth
              disabled
            />
            <TextField
              label="Start date"
              type="date"
              size="small"
              value={editForm.start_date}
              onChange={(e) => setEditForm((prev) => ({ ...prev, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              size="small"
              value={editForm.end_date}
              onChange={(e) => setEditForm((prev) => ({ ...prev, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={!!editForm.is_active}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              )}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onEditSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

